/**
 * El mapa de temas de la portada (MAPA-01, contrato en `plan/MAPA-01-contrato.md`).
 *
 * Es un fichero estático, `/mapa-temas.json`, que se genera con cada lote: no pasa por el API ni
 * cuesta nada. **Si falta o no cumple el contrato, la portada funciona igual sin el mapa**: por eso
 * la lectura nunca lanza y la validación descarta el fichero entero en vez de pintar medio mapa.
 */

export interface TextoBilingue {
  readonly es: string;
  readonly en: string;
}

export interface FuenteDelTema {
  readonly slug: string;
  readonly editor: string;
  readonly hechos: number;
}

export interface TemaDelMapa {
  readonly id: string;
  readonly nombre: TextoBilingue;
  /** Centro del tema, de 0 a 1; `y = 0` es arriba. */
  readonly x: number;
  readonly y: number;
  readonly hechos: number;
  readonly fuentes: readonly FuenteDelTema[];
  readonly fuentesTotal: number;
  /** Solo preguntas comprobadas, de 0 a 5. Sin ninguna, el tema se ve pero no se abre. */
  readonly preguntas: readonly TextoBilingue[];
  readonly pocosHechos: boolean;
  /** Todos sus hechos salen de un solo estudio (un tema con forma de «el informe de Alemania»). Opcional. */
  readonly unaSolaFuente?: boolean;
  /**
   * MAPA-02, opcional: los subtemas de un tema grande, con la misma forma que un tema y su `x`/`y`
   * en la proyección local del tema. Solo dos niveles: un subtema no trae subtemas.
   */
  readonly subtemas?: readonly TemaDelMapa[];
  /** MAPA-02, opcional: `[x, y, índice del subtema o -1]`, como mucho 600; el fondo de la segunda capa. */
  readonly puntosLocales?: readonly PuntoDelMapa[];
}

/** `[x, y, índice del tema]`; el índice es `-1` para los hechos sin tema. */
export type PuntoDelMapa = readonly [number, number, number];

export interface MapaDeTemas {
  readonly version: 1;
  readonly generadaConLote: string;
  readonly totales: {
    readonly hechos: number;
    readonly fuentes: number;
    readonly temas: number;
    readonly hechosSinTema: number;
  };
  readonly temas: readonly TemaDelMapa[];
  readonly puntos: readonly PuntoDelMapa[];
}

export const RUTA_DEL_MAPA = '/mapa-temas.json';

const esNumero = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const enUnidad = (v: unknown): v is number => esNumero(v) && v >= 0 && v <= 1;
const esObjeto = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const esTexto = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;
const esBilingue = (v: unknown): v is TextoBilingue => esObjeto(v) && esTexto(v['es']) && esTexto(v['en']);

function esTema(v: unknown): v is TemaDelMapa {
  if (!esObjeto(v)) return false;
  return (
    esTexto(v['id']) &&
    esBilingue(v['nombre']) &&
    enUnidad(v['x']) &&
    enUnidad(v['y']) &&
    esNumero(v['hechos']) &&
    esNumero(v['fuentesTotal']) &&
    typeof v['pocosHechos'] === 'boolean' &&
    (v['unaSolaFuente'] === undefined || typeof v['unaSolaFuente'] === 'boolean') &&
    Array.isArray(v['preguntas']) &&
    v['preguntas'].every(esBilingue) &&
    Array.isArray(v['fuentes']) &&
    v['fuentes'].every(f => esObjeto(f) && esTexto(f['slug']) && esTexto(f['editor']) && esNumero(f['hechos']))
  );
}

const esPuntoDe = (n: number) => (p: unknown) =>
  Array.isArray(p) && p.length === 3 && enUnidad(p[0]) && enUnidad(p[1]) && Number.isInteger(p[2]) && p[2] >= -1 && p[2] < n;

/**
 * La segunda capa de un tema (MAPA-02) es opcional, así que si no cumple el contrato se quita y el
 * tema se queda como en la v1; el resto del mapa no se descarta por ella.
 */
function sinSegundaCapaRota(tema: TemaDelMapa): TemaDelMapa {
  const { subtemas, puntosLocales, ...resto } = tema as TemaDelMapa & Record<string, unknown>;
  if (subtemas === undefined && puntosLocales === undefined) return tema;
  const subtemasBien =
    Array.isArray(subtemas) &&
    subtemas.length > 0 &&
    subtemas.every(esTema) &&
    Array.isArray(puntosLocales) &&
    puntosLocales.length <= 600 &&
    puntosLocales.every(esPuntoDe(subtemas.length));
  return subtemasBien ? tema : resto;
}

/**
 * Comprueba el contrato. Un punto que apunta a un tema que no existe invalida el fichero: sería
 * pintar hechos bajo el nombre equivocado, que es justo lo que Athena no hace.
 */
export function validarMapa(v: unknown): MapaDeTemas | null {
  if (!esObjeto(v) || v['version'] !== 1) return null;
  const { temas, puntos, totales } = v;
  if (!Array.isArray(temas) || temas.length === 0 || !temas.every(esTema)) return null;
  if (!esObjeto(totales) || !esNumero(totales['hechos']) || !esNumero(totales['fuentes'])) return null;
  if (!Array.isArray(puntos) || puntos.length > 3000) return null;
  if (!puntos.every(esPuntoDe(temas.length))) return null;
  return { ...(v as unknown as MapaDeTemas), temas: temas.map(sinSegundaCapaRota) };
}

export async function pedirMapa(): Promise<MapaDeTemas | null> {
  try {
    const r = await fetch(RUTA_DEL_MAPA);
    if (!r.ok) return null;
    return validarMapa(await r.json());
  } catch {
    return null;
  }
}

/** Radio del marcador: raíz cuadrada, porque los temas van de decenas a miles de hechos. */
export function radioDe(hechos: number, maximo: number): number {
  return 4 + 9 * Math.sqrt(Math.max(0, hechos) / Math.max(1, maximo));
}

export interface Caja2D {
  readonly x: number;
  readonly y: number;
  readonly ancho: number;
  readonly alto: number;
}

const solapan = (a: Caja2D, b: Caja2D) => a.x < b.x + b.ancho && b.x < a.x + a.ancho && a.y < b.y + b.alto && b.y < a.y + a.alto;

export type LadoDelNombre = 'abajo' | 'arriba' | 'derecha' | 'izquierda';

/**
 * Dónde va el nombre de cada tema para que se lea sin pisar nada. Primero el elegido, que siempre
 * lo lleva; después, de mayor a menor, se prueba debajo, encima, a la derecha y a la izquierda del
 * punto, y se queda el primer sitio que no pisa otro nombre ni el punto de otro tema y cabe en el
 * lienzo. Si no cabe en ningún lado, el nombre sale al pasar el ratón o con el foco. El ancho del
 * texto se estima (7,4 px por carácter a 13 px): basta para no montar etiquetas.
 */
export function etiquetasVisibles(
  temas: readonly TemaDelMapa[],
  idioma: keyof TextoBilingue,
  ancho: number,
  alto: number,
  primero?: string,
): ReadonlyMap<string, LadoDelNombre> {
  const maximo = Math.max(...temas.map(t => t.hechos));
  const puntos: Caja2D[] = temas.map(t => {
    const r = radioDe(t.hechos, maximo);
    return { x: t.x * ancho - r, y: t.y * alto - r, ancho: 2 * r, alto: 2 * r };
  });
  const puestas: Caja2D[] = [];
  const lados = new Map<string, LadoDelNombre>();
  const orden = [...temas].sort((a, b) => Number(b.id === primero) - Number(a.id === primero) || b.hechos - a.hechos);
  for (const t of orden) {
    // Los nombres largos parten en dos líneas (máximo 150 px de ancho, como en el CSS).
    const texto = t.nombre[idioma].length * 7.4;
    const lineas = Math.ceil(texto / 150);
    const w = Math.min(texto, 150) + 12;
    const h = 18 * lineas + 2;
    const r = radioDe(t.hechos, maximo);
    const cx = t.x * ancho;
    const cy = t.y * alto;
    const candidatos: readonly [LadoDelNombre, Caja2D][] = [
      ['abajo', { x: cx - w / 2, y: cy + r + 2, ancho: w, alto: h }],
      ['arriba', { x: cx - w / 2, y: cy - r - 2 - h, ancho: w, alto: h }],
      ['derecha', { x: cx + r + 6, y: cy - h / 2, ancho: w, alto: h }],
      ['izquierda', { x: cx - r - 6 - w, y: cy - h / 2, ancho: w, alto: h }],
    ];
    const libre = candidatos.find(
      ([, caja]) =>
        caja.x >= 0 &&
        caja.x + caja.ancho <= ancho &&
        caja.y >= 0 &&
        caja.y + caja.alto <= alto &&
        !puestas.some(p => solapan(p, caja)) &&
        !temas.some((otro, k) => otro.id !== t.id && puntos[k] !== undefined && solapan(puntos[k], caja)),
    );
    const elegido = libre ?? (t.id === primero ? candidatos[0] : undefined);
    if (elegido === undefined) continue;
    puestas.push(elegido[1]);
    lados.set(t.id, elegido[0]);
  }
  return lados;
}

/**
 * Separa los puntos de temas que caen casi encima unos de otros, para que se puedan pulsar y leer.
 * Unas pocas pasadas de empujar cada par que está demasiado cerca; los desplazamientos son de unos
 * píxeles y el tema sigue en su nube. Con el lienzo sin medir (ancho 0) no toca nada.
 */
const BORDE_X = 70;
const BORDE_Y = 22;

export function separarMarcadores(temas: readonly TemaDelMapa[], ancho: number, alto: number): readonly TemaDelMapa[] {
  if (ancho <= 2 * BORDE_X || alto <= 2 * BORDE_Y || temas.length === 0) return temas;
  const maximo = Math.max(...temas.map(t => t.hechos));
  const pos = temas.map(t => ({ x: t.x * ancho, y: t.y * alto, r: radioDe(t.hechos, maximo) }));
  for (let vuelta = 0; vuelta < 80; vuelta++) {
    let movido = false;
    for (let i = 0; i < pos.length; i++) {
      for (let j = i + 1; j < pos.length; j++) {
        const a = pos[i]!;
        const b = pos[j]!;
        const minimo = a.r + b.r + 40;
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let d = Math.hypot(dx, dy);
        if (d >= minimo) continue;
        if (d < 0.01) {
          dx = 1;
          dy = 0;
          d = 1;
        }
        const empuje = (minimo - d) / 2;
        a.x -= (dx / d) * empuje;
        a.y -= (dy / d) * empuje;
        b.x += (dx / d) * empuje;
        b.y += (dy / d) * empuje;
        movido = true;
      }
    }
    // Lejos de los bordes, para que el nombre quepa a algún lado del punto.
    for (const p of pos) {
      p.x = Math.min(ancho - BORDE_X, Math.max(BORDE_X, p.x));
      p.y = Math.min(alto - BORDE_Y, Math.max(BORDE_Y, p.y));
    }
    if (!movido) break;
  }
  return temas.map((t, k) => ({ ...t, x: (pos[k]?.x ?? 0) / ancho, y: (pos[k]?.y ?? 0) / alto }));
}

/** Como mucho, estos temas en la portada: menos es más, y más de doce ya no se leen de un vistazo. */
export const MAXIMO_DE_TEMAS = 12;

/** Un tema entra en la portada con tres preguntas o más: con una o dos no da para explorar. */
export const MINIMO_DE_PREGUNTAS = 3;

/**
 * Un nombre que parece una etiqueta de máquina y no palabras de una persona: `t03`, `cluster_7`,
 * `ai/chatbots`, `TOPIC-12`, sin letras… Esos temas no se enseñan nunca en la portada.
 */
export function pareceEtiquetaTecnica(nombre: string): boolean {
  const limpio = nombre.trim();
  if (!/\p{L}{2}/u.test(limpio)) return true;
  if (/[_/\\{}<>=|#@]/u.test(limpio)) return true;
  if (/^(t|topic|tema|cluster|grupo)[\s-]?\d+$/iu.test(limpio)) return true;
  if (/\b(cluster|topic|noise|outlier|misc)\b/iu.test(limpio)) return true;
  // Todo en mayúsculas y con cifras o guiones: un código, no un nombre («AI-2027», «GRP-3»).
  return limpio === limpio.toUpperCase() && /[\d-]/u.test(limpio);
}

/**
 * Lo que la portada enseña del mapa. El mapa está para que una organizadora entienda en segundos qué
 * puede averiguar y se ponga a preguntar, así que solo quedan los temas que llevan a preguntas: con
 * tres o más, sin el aviso de pocos hechos y con nombre de persona en los dos idiomas. Si sobran,
 * van antes los que salen de varios estudios que los de uno solo (esos suelen ser «el informe de
 * Alemania», no un tema), y después los de más preguntas y más hechos.
 *
 * Los puntos de los temas que se quedan fuera pasan a `-2`: siguen dibujándose, pero sin nombre.
 */
/** Cuánto da para preguntar un tema: sus preguntas, y uno más si se puede abrir en subtemas. */
const valorDePreguntas = (tema: TemaDelMapa) =>
  tieneSegundaCapa(tema) ? Math.max(tema.preguntas.length, MINIMO_DE_PREGUNTAS) + 1 : tema.preguntas.length;

export function temasParaLaPortada(mapa: MapaDeTemas): MapaDeTemas {
  const elegidos = mapa.temas
    .map((tema, i) => ({ tema, i }))
    .filter(
      ({ tema }) =>
        (tema.preguntas.length >= MINIMO_DE_PREGUNTAS || tieneSegundaCapa(tema)) &&
        !tema.pocosHechos &&
        !pareceEtiquetaTecnica(tema.nombre.es) &&
        !pareceEtiquetaTecnica(tema.nombre.en),
    )
    .sort(
      (a, b) =>
        Number(a.tema.unaSolaFuente === true) - Number(b.tema.unaSolaFuente === true) ||
        valorDePreguntas(b.tema) - valorDePreguntas(a.tema) ||
        b.tema.hechos - a.tema.hechos,
    )
    .slice(0, MAXIMO_DE_TEMAS)
    .sort((a, b) => a.i - b.i);
  const nuevoIndice = new Map(elegidos.map(({ i }, k) => [i, k]));
  return {
    ...mapa,
    temas: elegidos.map(({ tema }) => tema),
    // -1 sigue siendo «sin tema»; los puntos de los temas que no salen pasan a -2, para que el
    // lienzo los pinte como parte de la nube (teal tenue) y no como grano gris.
    puntos: mapa.puntos.map(([x, y, t]) => [x, y, t === -1 ? -1 : (nuevoIndice.get(t) ?? -2)] as const),
  };
}

/**
 * Estira el mapa para que ocupe todo el recuadro. La proyección deja la nube donde cae, a menudo en
 * una franja estrecha con hueco alrededor, y así todo sale apretado. Se toma la caja que contiene
 * del 2 % al 98 % de los puntos en cada eje (un punto suelto no encoge el resto) y se lleva a 0..1;
 * lo que queda fuera se pega al borde, donde la máscara del lienzo ya lo funde.
 */
export function encuadrar(mapa: MapaDeTemas): MapaDeTemas {
  if (mapa.puntos.length < 10) return mapa;
  const cuantil = (valores: number[], q: number) => {
    const orden = [...valores].sort((a, b) => a - b);
    return orden[Math.min(orden.length - 1, Math.floor(q * orden.length))] ?? 0;
  };
  const xs = mapa.puntos.map(p => p[0]);
  const ys = mapa.puntos.map(p => p[1]);
  const [x0, x1, y0, y1] = [cuantil(xs, 0.02), cuantil(xs, 0.98), cuantil(ys, 0.02), cuantil(ys, 0.98)];
  if (x1 - x0 < 0.01 || y1 - y0 < 0.01) return mapa;
  const fx = (x: number) => Math.min(1, Math.max(0, (x - x0) / (x1 - x0)));
  const fy = (y: number) => Math.min(1, Math.max(0, (y - y0) / (y1 - y0)));
  return {
    ...mapa,
    temas: mapa.temas.map(t => ({ ...t, x: fx(t.x), y: fy(t.y) })),
    puntos: mapa.puntos.map(([x, y, t]) => [fx(x), fy(y), t] as const),
  };
}

/**
 * Los subtemas que enseña la segunda capa (MAPA-02). Dentro de un tema basta una pregunta: el
 * subtema ya es concreto. Fuera los de nombre de máquina; primero los de varios estudios, como en
 * la portada, y como mucho doce. `pocosHechos` no filtra aquí: su umbral (menos de 60 hechos) es de
 * temas, y un subtema es por fuerza más pequeño (en prod, subtemas de 51 a 59 hechos con tres
 * preguntas comprobadas se quedaban fuera).
 */
export function subtemasUsables(tema: TemaDelMapa): readonly TemaDelMapa[] {
  return (tema.subtemas ?? [])
    .filter(s => s.preguntas.length > 0 && !pareceEtiquetaTecnica(s.nombre.es) && !pareceEtiquetaTecnica(s.nombre.en))
    .sort(
      (a, b) => Number(a.unaSolaFuente === true) - Number(b.unaSolaFuente === true) || b.preguntas.length - a.preguntas.length || b.hechos - a.hechos,
    )
    .slice(0, MAXIMO_DE_TEMAS);
}

/** Un tema ofrece acercarse cuando tiene al menos dos subtemas con algo que preguntar. */
export function tieneSegundaCapa(tema: TemaDelMapa): boolean {
  return subtemasUsables(tema).length >= 2;
}

/**
 * La segunda capa como un mapa más, para dibujarla con las mismas piezas que la primera: los
 * subtemas usables son sus temas y los `puntosLocales` su nube (los de subtemas que no salen, a -2).
 */
export function capaDelTema(mapa: MapaDeTemas, tema: TemaDelMapa): MapaDeTemas {
  const usables = subtemasUsables(tema);
  const todos = tema.subtemas ?? [];
  const nuevoIndice = new Map(usables.map((s, k) => [todos.indexOf(s), k]));
  return encuadrar({
    ...mapa,
    temas: usables,
    puntos: (tema.puntosLocales ?? []).map(([x, y, t]) => [x, y, t === -1 ? -1 : (nuevoIndice.get(t) ?? -2)] as const),
  });
}

/**
 * Cuántos puntos de decorado caben en cada capa (Iván, 2026-10-03: «muchos puntos y pocos se pueden
 * pulsar»). Eran 3.000 en la portada y 600 en un tema; con menos, la nube sigue leyéndose como nube,
 * lo que se pulsa destaca y el móvil pinta menos en cada fotograma.
 */
export const PUNTOS_EN_LA_PORTADA = 700;
export const PUNTOS_EN_UNA_CAPA = 300;

/**
 * Se queda con `maximo` puntos repartidos a lo largo de la lista, sin azar: los mismos en cada carga
 * y en la misma proporción por tema que antes (la lista no se reordena).
 */
export function aclarar(mapa: MapaDeTemas, maximo: number): MapaDeTemas {
  const n = mapa.puntos.length;
  if (n <= maximo) return mapa;
  return { ...mapa, puntos: mapa.puntos.filter((_, i) => Math.floor((i * maximo) / n) !== Math.floor(((i - 1) * maximo) / n)) };
}

/** Una pregunta del mapa, como punto que se pulsa: su texto y dónde va, de 0 a 1 como un tema. */
export interface PreguntaEnElMapa {
  readonly texto: string;
  readonly x: number;
  readonly y: number;
}

/** Un rectángulo en píxeles dentro de la capa de marcadores: un nombre, un marcador, otro punto. */
export interface Caja {
  readonly x: number;
  readonly y: number;
  readonly ancho: number;
  readonly alto: number;
}

const seTocan = (a: Caja, b: Caja) => a.x < b.x + b.ancho && b.x < a.x + a.ancho && a.y < b.y + b.alto && b.y < a.y + a.alto;

/** El punto de oro mide 8 px y su halo 3 más por lado: con 2 de aire, una caja de 18. */
const LADO_DEL_ORO = 18;
/** Cuántas posiciones de la espiral se prueban antes de rendirse con una pregunta. */
const INTENTOS = 60;

/**
 * Las preguntas de un tema, como puntos de oro alrededor de su marcador: lo que se puede pulsar en
 * la nube (además del tema), con destino real, la caja con la pregunta escrita.
 *
 * En espiral (ángulo áureo) desde el borde del marcador; el giro de salida depende del tema para
 * que dos temas vecinos no repitan el mismo dibujo. **Ningún punto tapa un nombre ni un marcador**
 * (2026-10-03): cada posición de la espiral se prueba contra `obstaculos` (las cajas medidas de los
 * nombres y marcadores) y contra `ocupados` (los puntos ya puestos, también de otros temas), y se
 * salta a la siguiente si toca algo. Los puestos se añaden a `ocupados`. Si en `INTENTOS` vueltas no
 * hay hueco, esa pregunta no sale como punto: sigue en el panel.
 */
export function preguntasAlrededor(
  tema: TemaDelMapa,
  idioma: 'es' | 'en',
  radio: number,
  ancho: number,
  alto: number,
  obstaculos: readonly Caja[] = [],
  ocupados: Caja[] = [],
): readonly PreguntaEnElMapa[] {
  if (ancho <= 0 || alto <= 0) return [];
  const giro = [...tema.id].reduce((s, c) => s + c.charCodeAt(0), 0) * 0.7;
  const cx = tema.x * ancho;
  const cy = tema.y * alto;
  const puestas: PreguntaEnElMapa[] = [];
  let paso = 0;
  for (const p of tema.preguntas) {
    for (; paso < INTENTOS; paso++) {
      const angulo = giro + paso * 2.399963;
      const distancia = radio + 16 + 5 * paso;
      const x = cx + Math.cos(angulo) * distancia;
      const y = cy + Math.sin(angulo) * distancia;
      const caja = { x: x - LADO_DEL_ORO / 2, y: y - LADO_DEL_ORO / 2, ancho: LADO_DEL_ORO, alto: LADO_DEL_ORO };
      const dentro = caja.x >= 0 && caja.y >= 0 && caja.x + caja.ancho <= ancho && caja.y + caja.alto <= alto;
      if (!dentro || obstaculos.some(o => seTocan(o, caja)) || ocupados.some(o => seTocan(o, caja))) continue;
      ocupados.push(caja);
      puestas.push({ texto: p[idioma], x: x / ancho, y: y / alto });
      paso++;
      break;
    }
  }
  return puestas;
}
